import type { GeoConnection, GeoConnectionResponse, GeoConnectionsJson } from 'src/models';
import type { Lov, RoadName, CommentType } from 'src/models/master';
import type { CommentFeature } from 'src/consts/comment_source_type';

export interface LovResponse {
  list_type: string;
  key: string;
  val: string;
  disp_order: number;
}

interface LovsResponse {
  car_kind: LovResponse[];
  role: LovResponse[];
}

interface KilopostMini {
  a: string;
  b: number;
  c: number;
  d: string;
  e: string;
  f: string;
  g: string;
  h: string;
  i: number;
  j: string;
  k: string;
  l: string;
  m: number;
  n: string | null;
  o: string | null;
  p: number;
}

interface KilopostColMapMini {
  a: string;
  b: string;
  c: string;
  d: string;
  e: string;
  f: string;
  g: string;
  h: string;
  i: string;
  j: string;
  k: string;
  l: string;
  m: string;
  n: string;
  o: string;
  p: string;
}

interface KilopostValMapMini {
  a: Record<string, string>;
  b: Record<string, string>;
  c: Record<string, string>;
  d: Record<string, string>;
  e: Record<string, string>;
  f: Record<string, string>;
  g: Record<string, string>;
  h: Record<string, string>;
  i: Record<string, number>;
  j: Record<string, string>;
  k: Record<string, string>;
  l: Record<string, string>;
  m: Record<string, number>;
  n: Record<string, string>;
  o: Record<string, string>;
  p: Record<string, number>;
}

export type RoadNameResponse = {
  id: number;
  kp_set_id: number;
  area: string;
  road_name_disp: string;
  road_name_disp_short: string;
  road_name: string;
  disp_order: number;
  directions: string[];
};

export type RoadNameRaw = Omit<RoadNameResponse, 'directions'> & {
  directions: string;
};

export interface CommentTypeResponse {
  id: number;
  group1_id: number;
  comment_type: string;
  comment_type_disp: string;
  disp_order: number;
  icon_path: string;
}

export interface KilopostsMaster {
  kiloposts: KilopostMini[];
  kilopost_col_map: KilopostColMapMini;
  kilopost_val_map: KilopostValMapMini;
}

export type MasterGetAllResponse = KilopostsMaster & {
  road_names: RoadNameRaw[];
  comment_types: CommentTypeResponse[];
  comment_type_icon_paths: string[];
  comment_features_by_source_type: Record<string, CommentFeature[]>;
  lovs: LovsResponse;
};

export const toLov = (response: LovResponse): Lov => ({
  listType: response.list_type,
  key: response.key,
  val: response.val,
  dispOrder: response.disp_order,
});

export const toRoadName = (response: RoadNameResponse): RoadName => ({
  id: response.id,
  kpSetId: response.kp_set_id,
  area: response.area,
  roadNameDisp: response.road_name_disp,
  roadNameDispShort: response.road_name_disp_short,
  roadName: response.road_name,
  dispOrder: response.disp_order,
  directions: response.directions,
});

const GEO_CONNECTION_KNOWN_KEYS = new Set(['src_kp_end', 'dst_kp_start', 'has_next', 'next_key']);

export const toGeoConnection = (response: GeoConnectionResponse): GeoConnection => {
  const nested: Record<string, GeoConnection> = {};
  for (const [key, value] of Object.entries(response)) {
    if (GEO_CONNECTION_KNOWN_KEYS.has(key)) continue;
    if (typeof value === 'object' && value !== null) {
      nested[key] = toGeoConnection(value as GeoConnectionResponse);
    }
  }
  return {
    srcKpEnd: response.src_kp_end,
    dstKpStart: response.dst_kp_start,
    hasNext: response.has_next,
    nextKey: response.next_key,
    nested,
  };
};

export const toGeoConnections = (json: GeoConnectionsJson): Record<string, Record<string, GeoConnection>> =>
  Object.fromEntries(
    Object.entries(json).map(([k1, v1]) => [
      k1,
      Object.fromEntries(Object.entries(v1).map(([k2, v2]) => [k2, toGeoConnection(v2)])),
    ]),
  );

export const toCommentType = (response: CommentTypeResponse): CommentType => ({
  id: response.id,
  group1Id: response.group1_id,
  commentType: response.comment_type,
  commentTypeDisp: response.comment_type_disp,
  dispOrder: response.disp_order,
  iconPath: response.icon_path,
});
